import {
  APP_LABEL,
  PLATFORM_HOST,
  PLATFORM_SLUG,
  PRODUCTS,
} from '@app/shared/brand'
import {
  classify,
  fill,
  findActiveContent,
  type HtmlKind,
  sanitize,
} from '@segnavia/html'
import type { Format } from './format'
import {
  findHtmlElements,
  parseHtml,
  removeHtmlNode,
  serializeHtml,
} from './html-parser'

const wholeDocument =
  /^\s*<!doctype\s+html[^>]*>\s*<html(?:\s[^>]*)?>[\s\S]*<\/html>\s*$/i

/** Stored HTML pages contain no form controls. */
const FORM_CONTROL_ELEMENTS: ReadonlySet<string> = new Set([
  'form',
  'input',
  'button',
  'select',
  'textarea',
])

const isFormControl = (element: { name: string }) =>
  FORM_CONTROL_ELEMENTS.has(element.name.toLowerCase())

export type HtmlPageKind = HtmlKind

const productionOrigins = new Set([
  `https://${PLATFORM_HOST}`,
  ...Object.values(PRODUCTS).flatMap((product) => [
    `https://${product.host}`,
    `https://${APP_LABEL}.${product.host}`,
  ]),
])

function htmlPageLinkOrigins(extra?: Iterable<string>): Set<string> {
  const origins = new Set(productionOrigins)
  for (const value of extra ?? []) {
    try {
      const url = new URL(value)
      if (url.protocol === 'https:') origins.add(url.origin.toLowerCase())
    } catch {}
  }
  return origins
}

/** Page authoring embeds non-platform fonts as data: faces. */
const pageSanitize = { dataFonts: true } as const

/**
 * A stylesheet construct Segnavia's sanitizer removes: `<style> …` or
 * `tag[style] …`, as `findActiveContent` reports them. Those never make a
 * page scripted.
 */
function isStylesheetConstruct(report: string): boolean {
  return report.startsWith('<style> ') || /^[^[\s]+\[style\] /.test(report)
}

function activeContent(
  body: string,
  options: { linkOrigins: Iterable<string>; keepScripts?: boolean },
): string[] {
  return findActiveContent(body, { ...options, ...pageSanitize }).filter(
    (item) => !isStylesheetConstruct(item),
  )
}

/** Fill page bindings with Adanim's names and allowed link origins. */
export function fillHtmlPageData(input: {
  body: string
  data: Record<string, unknown>
  ownHttpsOrigins?: Iterable<string>
  unavailable?: Iterable<string>
}) {
  return fill(input.body, {
    prefix: PLATFORM_SLUG,
    data: input.data,
    linkOrigins: htmlPageLinkOrigins(input.ownHttpsOrigins),
    unavailable: input.unavailable,
    ...pageSanitize,
  })
}

function sanitizePage(
  body: string,
  options: { linkOrigins: Iterable<string>; keepScripts?: boolean },
): string {
  return sanitize(body, { ...options, ...pageSanitize })
}

/** The active-content rule shared by every stored HTML page and slide. */
export function htmlIsScriptFree(
  body: string,
  extraOwnOrigins?: Iterable<string>,
): boolean {
  return (
    activeContent(body, {
      linkOrigins: htmlPageLinkOrigins(extraOwnOrigins),
    }).length === 0
  )
}

/** A page's execution model follows only from the content it stores. */
export function htmlPageKind(body: string): HtmlPageKind {
  return classify(body, PLATFORM_SLUG)
}

function htmlHasOnlyAllowedActiveContent(
  body: string,
  extraOwnOrigins?: Iterable<string>,
): boolean {
  return (
    findHtmlElements(parseHtml(body).children, isFormControl).length === 0 &&
    activeContent(body, {
      linkOrigins: htmlPageLinkOrigins(extraOwnOrigins),
      keepScripts: htmlPageKind(body) === 'scripted',
    }).length === 0
  )
}

/** Legacy reads stay inert even though their old body can no longer be saved. */
export function stripHtmlScripts(
  body: string,
  extraOwnOrigins?: Iterable<string>,
): string {
  return sanitizePage(body, {
    linkOrigins: htmlPageLinkOrigins(extraOwnOrigins),
  })
}

/** Remove disallowed active content while retaining a scripted page's scripts. */
export function sanitizeHtmlPage(
  body: string,
  extraOwnOrigins?: Iterable<string>,
): string {
  const sanitized = sanitizePage(body, {
    linkOrigins: htmlPageLinkOrigins(extraOwnOrigins),
    keepScripts: htmlPageKind(body) === 'scripted',
  })
  const document = parseHtml(sanitized)
  const controls = findHtmlElements(document.children, isFormControl)
  if (!controls.length) return sanitized
  for (const element of controls) removeHtmlNode(element)
  return serializeHtml(document)
}

export function htmlIsValid(
  body: string,
  extraOwnOrigins?: Iterable<string>,
): boolean {
  return (
    wholeDocument.test(body) &&
    htmlPageKind(body) !== 'refused' &&
    htmlHasOnlyAllowedActiveContent(body, extraOwnOrigins)
  )
}

/** Remove active and embedded content before deriving searchable words. */
export function htmlSearchText(body: string): string {
  return body
    .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, ' ')
    .replace(/data:[^\s"'<>)]*/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(?:nbsp|ensp|emsp);/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#(?:39|x27);/gi, "'")
    .replace(/\s+/g, ' ')
    .trim()
}

export const html = {
  bodyStorage: 'inline',
  validate: (body: string) => htmlIsValid(body),
  outline: () => [],
  searchText: htmlSearchText,
  tools: [],
} as const satisfies Format
